/**
 * The messages panel's standard look (prototype: .mpScrim and .mp over the app's chat screen;
 * room marks and filters from phone-signed-in/q1-both.html). Draws the behavior half's state and
 * calls its actions; holds no rule.
 *
 * The page never moves (spec "The panel never moves the page"): the panel and its scrim are fixed
 * under the site header and nothing locks the document's scroll; while open, the wheel over the
 * scrim, or over the panel outside a thread that can scroll, is held so the page behind stays put.
 * Escape and the scrim close it, and focus returns where it was.
 *
 * The stream scrolls as the app's and the phone's chat do (useChatThreadScroll, under the gate the
 * behavior half's stream shares): older history asked for early, the reader's place held.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type MutableRefObject } from 'react';
import { motion } from 'framer-motion';
import { ClampedBody } from '@/components/chat/ClampedBody';
import { FoldPanel } from '@/components/chat/FoldPanel';
import { RevealLine } from '@/components/chat/RevealLine';
import { SEND_GLYPH_PATH, useChatComposer } from '@/components/chat/useChatComposer';
import { useChatThreadScroll, type ChatThreadGate } from '@/hooks/chat/useChatThreadScroll';
import { CHAT_NOT_FOUND_LINE, chatEmptyState } from '@/lib/client/clientChatThread';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { contract, type MessagesFilterId, type PanelMessage } from './contract';
import type { MessagesPanelRuntime } from './useMessagesPanelPiece';
import css from './MessagesPanel.module.css';

const part = partsOf(contract);

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

const svg = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const LockIcon = () => (
  <svg {...svg} aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const PlaneIcon = () => (
  <svg {...svg} strokeWidth={2} aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

const CloseIcon = () => (
  <svg {...svg} aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const ChevronRight = () => (
  <svg {...svg} aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const TickIcon = () => (
  <svg {...svg} aria-label="Stored" role="img">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg {...svg} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/** The room beside a message's time: the lock for the Secure room, the Telegram mark for the Telegram chat. */
function RoomMark({ room }: { room: PanelMessage['room'] }) {
  return (
    <span className={css.mark} role="img" aria-label={room === 'secure' ? 'Secure' : 'Telegram'} {...part('roomMark')}>
      {room === 'secure' ? <LockIcon /> : <PlaneIcon />}
    </span>
  );
}

/** A link inside a notice opens in a new tab, never in place of the site. */
function interceptLink(event: MouseEvent) {
  const anchor = (event.target as Element).closest?.('a');
  if (!anchor) return;
  event.preventDefault();
  const href = anchor.getAttribute('href');
  if (href && /^https?:\/\//i.test(href)) window.open(href, '_blank', 'noopener,noreferrer');
}

function Notice({ message, onDoor }: { message: PanelMessage; onDoor: (href: string) => void }) {
  const [prepOpen, setPrepOpen] = useState(false);
  const pages = message.preparation;
  return (
    <div className={cx(css.msg, css.them, css.auto)} data-entry-id={message.id} {...part('notice')}>
      <ClampedBody className={css.autoBody} moreClassName={css.showMore} measureKey={message.html ?? message.text} onClick={interceptLink}>
        {message.html === null ? message.text : <span dangerouslySetInnerHTML={{ __html: message.html }} />}
      </ClampedBody>
      {message.state && (
        <div className={css.state} {...part('stateLine')}>
          {message.state}
        </div>
      )}
      {pages.length > 0 && (
        <div {...part('preparation')}>
          <button type="button" className={css.fold} aria-expanded={prepOpen} onClick={() => setPrepOpen((open) => !open)}>
            {message.preparationLabel ?? 'How to prepare'}
            <ChevronRight />
          </button>
          <FoldPanel open={prepOpen}>
            {pages.map((page) => (
              <div key={page.id} className={css.foldPage} onClick={interceptLink}>
                {page.html === null ? 'How to prepare' : <span dangerouslySetInnerHTML={{ __html: page.html }} />}
              </div>
            ))}
          </FoldPanel>
        </div>
      )}
      <div className={cx(css.foot, !message.door && css.footEnd)}>
        {message.door && (
          <button type="button" className={css.door} onClick={() => onDoor(message.door!.href)} {...part('door')}>
            {message.door.label}
            <ChevronRight />
          </button>
        )}
        <span className={css.time}>
          <RoomMark room={message.room} />
          {message.time}
        </span>
      </div>
    </div>
  );
}

function Message({ message, onRetry }: { message: PanelMessage; onRetry: (id: string) => void }) {
  const mine = message.from === 'you';
  return (
    <>
      <div className={cx(css.msg, mine ? css.me : css.them)} data-entry-id={message.id} {...part('message')}>
        <span className={css.text}>{message.text}</span>
        <div className={css.meta}>
          <RoomMark room={message.room} />
          {message.delivery === 'sending' ? 'Sending…' : message.time}
          {message.delivery === 'stored' && <TickIcon />}
        </div>
      </div>
      {message.delivery === 'unsent' && (
        <RevealLine className={css.failReveal}>
          <button type="button" className={css.failNote} onClick={() => onRetry(message.id)} {...part('sendIssue')}>
            <AlertIcon />
            Not sent. Click to try again.
          </button>
        </RevealLine>
      )}
      {message.delivery === 'refused' && (
        <RevealLine className={css.failReveal}>
          <span className={css.failNote} role="status" {...part('sendIssue')}>
            <AlertIcon />
            Not sent
          </span>
        </RevealLine>
      )}
    </>
  );
}

function Composer({ runtime, onSend, onFocus }: { runtime: MessagesPanelRuntime; onSend: (text: string) => void; onFocus: () => void }) {
  const { state, actions } = runtime;
  const composer = useChatComposer({ draftScope: state.draftScope, onSend, onType: actions.typing });
  const { text, change, inputRef, pillRef, live, flying, tooLong } = composer;
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter starts a new line (as the prototype's composer).
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    composer.sendButton.onClick();
  };
  return (
    <div className={css.composer} {...part('composer')}>
      {tooLong && <p className={css.limit} role="status">Keep it under 4096 characters.</p>}
      <div className={css.pill} ref={pillRef} onClick={composer.focusPill}>
        <textarea
          ref={inputRef}
          className={css.input}
          rows={1}
          value={text}
          onChange={change}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          placeholder={state.studioName ? `Message ${state.studioName}` : 'Message'}
          aria-label="Message"
        />
      </div>
      <motion.button
        type="button"
        className={cx(css.send, !(live || flying) && css.sendIdle)}
        aria-label="Send"
        {...composer.sendButton}
        {...part('send')}
      >
        <motion.svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={composer.glyphStyle}>
          <path transform="rotate(-45 12 12)" d={SEND_GLYPH_PATH} />
        </motion.svg>
      </motion.button>
    </div>
  );
}

interface MessagesPanelViewProps extends MessagesPanelRuntime {
  /** The stream's rest gate, shared with the behavior half's stream (useWebChatThread). */
  readonly gate: ChatThreadGate;
}

interface ScreenProps {
  readonly runtime: MessagesPanelRuntime;
  readonly gate: ChatThreadGate;
  /** The thread, for the panel's wheel hold. */
  readonly streamRef: MutableRefObject<HTMLDivElement | null>;
}

/**
 * The panel's head, filters, stream and composer, drawn from the first open on. The stream's scroll
 * is the app's and the phone's (useChatThreadScroll): pinned to the newest message, older history
 * asked for four screens early, and the message being read held in place while older ones join
 * above it or the stream moves.
 */
function Screen({ runtime, gate, streamRef }: ScreenProps) {
  const { state, actions, options } = runtime;
  const entries = useMemo(() => state.days.flatMap((day) => day.messages), [state.days]);
  const currentFilter = state.filters.find((f) => f.current);
  const { threadRef, innerRef, onScroll, pin, startNewList } = useChatThreadScroll({
    gate, entries, hasOlder: state.hasOlder, loadOlder: actions.loadOlder, room: 'secure', lens: currentFilter?.id ?? 'all',
  });
  useLayoutEffect(() => {
    streamRef.current = threadRef.current;
  });

  // Opened at a message (a banner's): it comes into view once it is on screen, and is picked out for a moment.
  const focusedRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!state.focusId) {
      focusedRef.current = null;
      return;
    }
    if (focusedRef.current === state.focusId) return;
    const thread = threadRef.current;
    const el = thread?.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(state.focusId)}"]`);
    if (!thread || !el) return;
    focusedRef.current = state.focusId;
    thread.scrollTop += el.getBoundingClientRect().top - thread.getBoundingClientRect().top - 24;
    el.classList.add(css.flash);
    setTimeout(() => el.classList.remove(css.flash), 1900);
  }, [entries, state.focusId, threadRef]);

  // Another filter opens at its newest message; a send lands at the newest end.
  const chooseFilter = (id: MessagesFilterId) => {
    if (id === currentFilter?.id) return;
    startNewList();
    actions.chooseFilter(id);
  };
  const send = (text: string) => {
    pin();
    actions.send(text);
  };

  const name = state.studioName || 'the studio';
  const empty = state.status === 'ready' && entries.length === 0 && currentFilter ? chatEmptyState(currentFilter.id) : null;

  return (
    <div className={css.screen}>
      <div className={css.hdr} {...part('header')}>
        <span aria-hidden="true">
          {state.logoUrl && <img className={css.logo} src={state.logoUrl} alt="" {...part('logo')} />}
        </span>
        <div className={css.who}>
          <h2 className={css.whoName} id="web-messages-name" {...part('name')}>
            {state.studioName}
          </h2>
          <div className={css.rooms} {...part('rooms')}>
            <span className={css.leg}>
              <LockIcon />
              Secure
            </span>
            <span className={css.dot} aria-hidden="true">
              ·
            </span>
            <span className={css.leg}>
              <PlaneIcon />
              Telegram
            </span>
          </div>
        </div>
        <button type="button" className={css.close} aria-label="Close" onClick={actions.close} {...part('close')}>
          <CloseIcon />
        </button>
      </div>

      {options.showFilters && (
        <div className={css.chips} role="group" aria-label="Filter messages" {...part('filters')}>
          {state.filters.map((f) => (
            <button
              key={f.id}
              type="button"
              className={css.chip}
              aria-pressed={f.current}
              onClick={() => chooseFilter(f.id)}
              {...part('filter', f.current && 'selected')}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className={css.thread} ref={threadRef} onScroll={onScroll} {...part('thread')}>
        <div className={css.threadInner} ref={innerRef}>
          {state.loadingOlder && (
            <div className={css.stateRow} {...part('older')}>
              <div className={css.spinner} />
            </div>
          )}
          {state.status === 'loading' && (
            <div className={css.stateRow} role="status" aria-label="Loading messages" {...part('loading')}>
              <div className={css.spinner} />
            </div>
          )}
          {state.status === 'not-found' && (
            <div className={css.stateRow} {...part('unavailable')}>
              {CHAT_NOT_FOUND_LINE}
            </div>
          )}
          {state.status === 'error' && (
            <div className={css.stateRow} {...part('error')}>
              Your messages did not load.
              <button type="button" className={css.retryBtn} onClick={actions.retry}>
                Try again
              </button>
            </div>
          )}
          {empty?.kind === 'explainer' && (
            <div className={css.explain} {...part('empty')}>
              <div className={css.explainIcon}>
                <LockIcon />
              </div>
              <h3 className={css.explainTitle}>Secure messaging</h3>
              <p className={css.explainBody}>
                Messages you send here stay between you and {name}. They never pass through Telegram.
              </p>
            </div>
          )}
          {empty?.kind === 'line' && (
            <div className={css.stateRow} {...part('empty')}>
              {empty.text}
            </div>
          )}
          {state.status === 'ready' &&
            state.days.map((day) => (
              <Fragment key={day.key}>
                <div className={css.day} role="separator" {...part('day')}>
                  {day.label}
                </div>
                {day.messages.map((message) =>
                  message.kind === 'notice' ? (
                    <Notice key={message.id} message={message} onDoor={actions.openDoor} />
                  ) : message.kind === 'media' ? (
                    <div key={message.id} className={css.sys} data-entry-id={message.id} {...part('media')}>
                      <RoomMark room={message.room} />
                      {message.text}
                    </div>
                  ) : (
                    <Message key={message.id} message={message} onRetry={actions.retrySend} />
                  ),
                )}
              </Fragment>
            ))}
        </div>
      </div>

      {state.typing && (
        <p className={css.typing} role="status" {...part('typing')}>
          {name} is typing…
        </p>
      )}
      <Composer runtime={runtime} onSend={send} onFocus={pin} />
    </div>
  );
}

export function MessagesPanelView({ gate, ...runtime }: MessagesPanelViewProps) {
  const { state, actions } = runtime;
  const panelRef = useRef<HTMLElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement | null>(null);
  const lastFocus = useRef<Element | null>(null);
  const { open } = state;
  // Drawn from the first open on, so closing slides the stream out instead of an empty panel.
  const [drawn, setDrawn] = useState(open);
  useEffect(() => {
    if (open) setDrawn(true);
  }, [open]);

  const closeRef = useRef(actions.close);
  closeRef.current = actions.close;

  // Focus moves into the panel on open and back on close; Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    const timer = setTimeout(() => panelRef.current?.focus({ preventScroll: true }), 30);
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      const back = lastFocus.current;
      if (back instanceof HTMLElement && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [open]);

  // The page behind holds still: the wheel over the scrim, or over the panel outside a thread that can scroll. A finger
  // still scrolls a row that runs sideways, such as the filters on a phone (card 4 walk, Gabriel 2026-11-01).
  useEffect(() => {
    if (!open) return undefined;
    const hold = (event: Event) => {
      const thread = streamRef.current;
      if (thread && event.target instanceof Node && thread.contains(event.target) && thread.scrollHeight > thread.clientHeight + 1) return;
      if (event.type === 'touchmove' && event.target instanceof Element) {
        for (let el: Element | null = event.target; el && el !== event.currentTarget; el = el.parentElement) {
          if (el.scrollWidth > el.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return;
        }
      }
      event.preventDefault();
    };
    const targets = [scrimRef.current, panelRef.current].filter((el): el is HTMLElement => el !== null);
    for (const el of targets) {
      el.addEventListener('wheel', hold, { passive: false });
      el.addEventListener('touchmove', hold, { passive: false });
    }
    return () => {
      for (const el of targets) {
        el.removeEventListener('wheel', hold);
        el.removeEventListener('touchmove', hold);
      }
    };
  }, [open]);

  return (
    <div className={cx(open && css.open)} {...pieceRoot(contract)}>
      <div className={css.scrim} ref={scrimRef} aria-hidden="true" onClick={actions.close} {...part('scrim')} />
      <aside
        ref={panelRef}
        className={css.panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="false"
        aria-labelledby="web-messages-name"
        aria-hidden={!open}
        id="web-messages"
        {...part('panel')}
      >
        <div className={css.page}>
          <div className={css.atmosphere} aria-hidden="true" />
          {(open || drawn) && <Screen runtime={runtime} gate={gate} streamRef={streamRef} />}
        </div>
      </aside>
    </div>
  );
}
